import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  CreditCard,
  FileCheck2,
  LockKeyhole,
  ShieldAlert,
} from "lucide-react";
import Headerdash from "../pages/head";
import Sidebar from "../pages/sidebar";

const getPrimaryRole = (role) => (Array.isArray(role) ? role[0] : role);

const getPlanState = (role) => {
  const primaryRole = getPrimaryRole(role);

  if (primaryRole === 7) {
    return {
      eyebrow: "Plan access paused",
      title: "Plan expired or no plan selected",
      description:
        "Choose a subscription plan to unlock mock tests, practice questions, AI scoring, and your personalized study workspace.",
      action: "Choose a plan",
    };
  }

  if (primaryRole === 5 || primaryRole === 6) {
    return {
      eyebrow: "Upgrade available",
      title: "Upgrade your plan to Premium",
      description:
        "Move to Premium to access the full PrepNexis experience, including more mocks, section practice, and AI-assisted preparation.",
      action: "Upgrade plan",
    };
  }

  return {
    eyebrow: "Subscription required",
    title: "No active plan found",
    description:
      "Select a PrepNexis plan to continue with your PTE preparation tools.",
    action: "View plans",
  };
};

const UpgradeShell = ({ children }) => (
  <div className="min-h-screen bg-slate-50 text-gray-700">
    <Headerdash />
    <Sidebar />
    <main className="min-h-screen pt-20 sm:pl-72">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] w-full max-w-7xl items-center px-4 py-6 sm:px-6 lg:px-8">
        {children}
      </div>
    </main>
  </div>
);

const benefitItems = [
  "Continue your PTE preparation without losing momentum",
  "Access mock tests and section-wise practice",
  "Track results from one dashboard workspace",
];

export default function UpgradeYourPlan({ role }) {
  const navigate = useNavigate();
  const planState = getPlanState(role);

  return (
    <UpgradeShell>
      <section className="grid w-full gap-6 lg:grid-cols-[minmax(280px,0.9fr),1.1fr] lg:items-stretch">
        <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-semibold uppercase text-purple-700">
                {planState.eyebrow}
              </p>
              <h1 className="mt-2 text-2xl font-extrabold text-gray-900 sm:text-3xl">
                {planState.title}
              </h1>
              <p className="mt-3 text-sm leading-6 text-gray-500 sm:text-base">
                {planState.description}
              </p>
            </div>
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
              <ShieldAlert className="h-6 w-6" aria-hidden="true" />
            </span>
          </div>

          <div className="mt-6 grid gap-3">
            {benefitItems.map((item) => (
              <div
                key={item}
                className="flex items-start gap-3 rounded-md bg-slate-50 p-3"
              >
                <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md bg-white text-purple-700 shadow-sm">
                  <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                </span>
                <p className="text-sm font-semibold text-gray-700">{item}</p>
              </div>
            ))}
          </div>

          <div className="mt-7 flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={() => navigate("/billing")}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-violet-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
            >
              {planState.action}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="inline-flex min-h-11 items-center justify-center rounded-md bg-white px-4 py-2.5 text-sm font-bold text-gray-700 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-2"
            >
              Back to dashboard
            </button>
          </div>
        </div>

        <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="rounded-md bg-purple-50 p-5">
            <span className="grid h-12 w-12 place-items-center rounded-md bg-white text-purple-700 shadow-sm">
              <LockKeyhole className="h-6 w-6" aria-hidden="true" />
            </span>
            <h2 className="mt-5 text-xl font-extrabold text-gray-900">
              Your workspace is waiting
            </h2>
            <p className="mt-2 text-sm leading-6 text-gray-600">
              Once a plan is active, your dashboard, mock tests, practice
              material, and result history become available again.
            </p>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
              <span className="grid h-10 w-10 place-items-center rounded-md bg-white text-purple-700 shadow-sm">
                <CreditCard className="h-5 w-5" aria-hidden="true" />
              </span>
              <p className="mt-4 text-sm font-semibold uppercase text-gray-500">
                Billing
              </p>
              <p className="mt-1 text-lg font-extrabold text-gray-900">
                Flexible plans
              </p>
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
              <span className="grid h-10 w-10 place-items-center rounded-md bg-white text-purple-700 shadow-sm">
                <FileCheck2 className="h-5 w-5" aria-hidden="true" />
              </span>
              <p className="mt-4 text-sm font-semibold uppercase text-gray-500">
                Access
              </p>
              <p className="mt-1 text-lg font-extrabold text-gray-900">
                Mock tests
              </p>
            </div>
          </div>
        </div>
      </section>
    </UpgradeShell>
  );
}
